/**
 * Client-side metrics utilities.
 * Uses Navigator.sendBeacon API to reliably send metrics even during page navigation.
 */

import { withDeploymentParam } from '@/config/deployment'
import { isE2EObservabilityDisabled } from '@/lib/observability/test-mode'

type MetricsEndpoint =
  | '/api/t/transaction'
  | '/api/t/rpc'
  | '/api/t/load-time'
  | '/api/t/wallet-connection'

/**
 * Sends metrics payload to the server using sendBeacon.
 * Falls back to fetch with keepalive if sendBeacon fails.
 * Silently fails to ensure metrics never break the app.
 */
export function sendMetricBeacon<T extends object>(endpoint: MetricsEndpoint, payload: T): void {
  if (isE2EObservabilityDisabled()) return

  try {
    const body = JSON.stringify(payload)
    const url = withDeploymentParam(endpoint)
    const sent = navigator.sendBeacon(url, body)

    // Fallback to fetch if sendBeacon fails (rare)
    if (!sent) {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      }).catch(() => {
        // Silent fail - metrics should never break the app
      })
    }
  } catch {
    // Silent fail - metrics should never break the app
  }
}

/**
 * Creates a simple timer for measuring operation duration.
 * Returns elapsed time in seconds.
 *
 * @example
 * const timer = createDurationTimer();
 * await doSomething();
 * const durationSeconds = timer.elapsed();
 */
export function createDurationTimer() {
  const startTime = performance.now()

  return {
    /**
     * Returns elapsed time in seconds since timer creation.
     */
    elapsed: (): number => (performance.now() - startTime) / 1000
  }
}
